import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import { Select, Space, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import HammerIcon from "src/assets/icons/HammerIcon";
import { Spinner } from "src/core/components/Spinner";
import { actionWithConfirm } from "src/core/utils/confirm";
import { AlertIconCell } from "src/modules/GlobalManualAdjustmentsManager/components/AlertIconCell";
import { SimpleInputCell } from "src/modules/GlobalManualAdjustmentsManager/components/SimpleInputCell";
import { SimpleNumberInputCell } from "src/modules/GlobalManualAdjustmentsManager/components/SimpleNumberInputCell";
import { CostLevelEnum } from "src/modules/GlobalManualAdjustmentsManager/enums/enums";
import { useGlobalManualAdjustmentsMetadata } from "src/modules/GlobalManualAdjustmentsManager/hooks/useGlobalManualAdjustmentsMetadata";
import { GlobalManualAdjustmentItem } from "src/modules/GlobalManualAdjustmentsManager/types/types";
import { ValueWithCurrency } from "src/modules/ValueWithCurrency/ValueWithCurrency";
import { masterInfoState } from "src/recoil/atoms";
import { OptionItem } from "src/types/common";

export function useGlobalManualAdjustmentsColumns(
  saveRecord: (
    record: GlobalManualAdjustmentItem,
    property: string,
    value: string | number | undefined | boolean,
  ) => Promise<void>,
  deleteRecord: (record: GlobalManualAdjustmentItem) => Promise<void>,
  deactivateAlert: (
    data: GlobalManualAdjustmentItem,
    alertId: number,
  ) => Promise<void>,
  duplicateRecord: (record: GlobalManualAdjustmentItem) => Promise<void>,
  deleteUnsavedRecord?: () => void,
) {
  const { t } = useTranslation();

  const masterInfoData = useRecoilValue(masterInfoState);

  const { typeOfCosts, costLevels, costTypeMappings } =
    useGlobalManualAdjustmentsMetadata();

  const materialsList = useMemo(
    () => masterInfoData.materials,
    [masterInfoData.materials],
  );

  const madeInsList = useMemo(
    () => masterInfoData.madeIns,
    [masterInfoData.madeIns],
  );

  /*const shouldCellUpdate = useCallback(
    (
      key: keyof GlobalManualAdjustmentItem,
      record: GlobalManualAdjustmentItem,
      prevRecord: GlobalManualAdjustmentItem,
    ) => {
      return (
        record[key] !== prevRecord[key] ||
        record.isInAction !== prevRecord.isInAction
      );
    },
    [],
  );*/

  const tableColumns = useMemo(() => {
    let columns: ColumnsType<GlobalManualAdjustmentItem> = [
      {
        title: <span></span>,
        key: "alert_column",
        align: "center",
        width: 40,
        render: (_: any, item: GlobalManualAdjustmentItem) => (
          <AlertIconCell alertId={item?.alertId} />
        ),
      },
      {
        title: t("tableColumn.modelNumber"),
        key: "modelNumber_column",
        align: "center",
        width: 140,

        render: (_value, item, index) => (
          <SimpleInputCell
            item={item}
            disabled={item.isInAction ?? false}
            defaultValue={item.model}
            onSave={async (value, item) => {
              await saveRecord(item, "model", value);
            }}
          />
        ),
      },
      {
        title: t("tableColumn.madeInId"),
        key: "madeInId_column",
        align: "center",
        width: 140,
        render: (
          _value: GlobalManualAdjustmentItem,
          item: GlobalManualAdjustmentItem,
        ) => (
          <Select
            disabled={item.isInAction}
            value={item.madeInId}
            style={{ width: "100%" }}
            fieldNames={{ value: "id", label: "label" }}
            onChange={async (
              _val: number,
              _option: OptionItem | OptionItem[],
            ) => {
              await saveRecord(item, "madeInId", _val);
            }}
            options={madeInsList}
          />
        ),
      },
      {
        title: t("tableColumn.costLevel"),
        key: "madeInId_column",
        align: "center",
        width: 100,
        render: (
          _value: GlobalManualAdjustmentItem,
          item: GlobalManualAdjustmentItem,
        ) => (
          <Select
            disabled={item.isInAction}
            value={item.costLevel}
            style={{ width: "100%" }}
            fieldNames={{ value: "id" }}
            onChange={async (_val: CostLevelEnum) => {
              await saveRecord(item, "costLevel", _val);
            }}
            options={costLevels}
          />
        ),
      },
      {
        title: t("tableColumn.description"),
        key: "description_column",
        align: "center",
        width: 160,
        render: (_value, item, index) => (
          <SimpleInputCell
            item={item}
            disabled={item.isInAction}
            defaultValue={item.description}
            onSave={async (value, item) => {
              await saveRecord(item, "description", value);
            }}
          />
        ),
      },
      {
        title: t("tableColumn.typeOfCost"),
        key: "typeOfCost_column",
        align: "center",
        width: 80,
        render: (
          _value: GlobalManualAdjustmentItem,
          item: GlobalManualAdjustmentItem,
        ) => (
          <Select
            disabled={item.isInAction}
            value={item.typeOfCost}
            style={{ width: "100%" }}
            fieldNames={{ value: "value", label: "value" }}
            onChange={async (_val: string) => {
              await saveRecord(item, "typeOfCost", _val);
            }}
            options={typeOfCosts}
          />
        ),
      },
      {
        title: t("tableColumn.costTypeMapping"),
        key: "costTypeMapping_column",
        align: "center",
        width: 80,
        render: (
          _value: GlobalManualAdjustmentItem,
          item: GlobalManualAdjustmentItem,
        ) => (
          <Select
            disabled={item.isInAction}
            value={item.costTypeMapping}
            style={{ width: "100%" }}
            fieldNames={{ value: "value", label: "value" }}
            onChange={async (_val: string) => {
              await saveRecord(item, "costTypeMapping", _val);
            }}
            options={costTypeMappings}
          />
        ),
      },
      {
        title: t("tableColumn.father"),
        key: "father_column",
        align: "center",
        width: 140,
        render: (_value, item, index) => (
          <SimpleInputCell
            item={item}
            disabled={item.isInAction}
            defaultValue={item.father}
            onSave={async (value, item) => {
              await saveRecord(item, "father", value);
            }}
          />
        ),
      },
      {
        title: t("tableColumn.component"),
        key: "component_column",
        align: "center",
        width: 140,
        render: (_value, item, index) => (
          <SimpleInputCell
            item={item}
            disabled={item.isInAction}
            defaultValue={item.component}
            onSave={async (value, item) => {
              await saveRecord(item, "component", value);
            }}
          />
        ),
      },
      {
        title: t("tableColumn.componentMaterial"),
        key: "componentMaterial_column",
        align: "center",
        width: 110,
        render: (
          _value: GlobalManualAdjustmentItem,
          item: GlobalManualAdjustmentItem,
        ) => (
          <Select
            disabled={item.isInAction}
            value={item.componentMaterialId}
            style={{ width: "100%" }}
            fieldNames={{ value: "id", label: "label" }}
            onChange={async (
              _val: number,
              _option: OptionItem | OptionItem[],
            ) => {
              await saveRecord(item, "componentMaterialId", _val);
            }}
            options={materialsList}
          />
        ),
      },
      {
        title: t("tableColumn.adjustmentCost"),
        key: "adjustmentCost_column",
        align: "center",
        width: 110,
        render: (
          _value: GlobalManualAdjustmentItem,
          item: GlobalManualAdjustmentItem,
        ) => (
          <SimpleNumberInputCell
            item={item}
            disabled={item.isInAction}
            defaultValue={item.adjustedCost}
            onSave={async (value, item) => {
              await saveRecord(item, "adjustedCost", value);
            }}
          />
        ),
      },
      {
        title: t("tableColumn.ovh"),
        key: "ovh_column",
        align: "center",
        width: 80,
        render: (
          _value: GlobalManualAdjustmentItem,
          item: GlobalManualAdjustmentItem,
          _index: number,
        ) => (
          <ValueWithCurrency
            value={item.ovhCost}
            currencyCode={item.currencyCode}
            showEmptyIfNullOrZero={true}
            showCurrency={true}
          />
        ),
      },
      {
        title: t("tableColumn.cost"),
        key: "cost_column",
        align: "center",
        width: 80,
        render: (
          _value: GlobalManualAdjustmentItem,
          item: GlobalManualAdjustmentItem,
        ) => (
          <ValueWithCurrency
            value={item.fullCost}
            currencyCode={item.currencyCode}
            showEmptyIfNullOrZero={true}
            showCurrency={true}
          />
        ),
      },
      {
        title: <span></span>,
        key: "alert-actions",
        width: 40,
        align: "center",
        render: (_, record) => (
          <Space size="middle">
            {record.alertId && (
              <Tooltip
                title={t("message.deactivateAlert")}
                overlayStyle={{ fontSize: "12px" }}
              >
                <HammerIcon
                  title={t("message.deactivateAlert")}
                  onClick={async () => {
                    if (record.id > -1 && record.alertId !== undefined) {
                      actionWithConfirm(
                        t("confirm.alertDeactivateTitle"),
                        t("confirm.alertDeactivateContent"),
                        async () =>
                          await deactivateAlert(record, record.alertId!),
                        t("button.continue"),
                      );
                    }
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    cursor: "pointer",
                    color: "black",
                  }}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        key: "manual-input-actions",
        width: 50,
        fixed: "right",
        align: "center",
        render: (_, record) => (
          <Space size="middle">
            {record.isInAction && <Spinner size="small" />}
            {!record.isInAction && (
              <React.Fragment>
                <Tooltip
                  title={t("button.duplicate")}
                  overlayStyle={{ fontSize: "12px" }}
                >
                  <CopyOutlined
                    onClick={async () => {
                      await duplicateRecord(record);
                    }}
                    style={{
                      cursor: "pointer",
                      color: "black",
                      fontSize: 17,
                    }}
                  />
                </Tooltip>
                <Tooltip
                  title={t("button.delete")}
                  overlayStyle={{ fontSize: "12px" }}
                >
                  <DeleteOutlined
                    onClick={async () => {
                      if (record.id > -1) {
                        actionWithConfirm(
                          t("confirm.recordDeleteConfirmTitle"),
                          t("confirm.recordDeleteConfirmContent"),
                          async () => await deleteRecord(record),
                        );
                      } else {
                        deleteUnsavedRecord?.();
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      color: "black",
                      fontSize: 17,
                    }}
                  />
                </Tooltip>
              </React.Fragment>
            )}
          </Space>
        ),
      },
    ];

    return columns;
  }, [
    costLevels,
    costTypeMappings,
    deactivateAlert,
    deleteRecord,
    deleteUnsavedRecord,
    duplicateRecord,
    madeInsList,
    materialsList,
    saveRecord,
    t,
    typeOfCosts,
  ]);

  return {
    tableColumns,
  };
}
